
import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  Alert,
  Keyboard,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';
import { useLanguage } from '@/contexts/LanguageContext';
import OnboardingLayout from '@/components/OnboardingLayout';
import { useOnboardingStepTracking } from '@/hooks/useOnboardingStepTracking';
import CircularArrowButton from '@/components/CircularArrowButton';
import { TempOnboardingService } from '@/services/tempOnboardingService';

type FrequencyOption = 'once_week' | 'two_three_week' | 'four_five_week' | 'daily' | 'already_sober' | 'not_addicted';

export default function OnboardingFrequencyScreen() {
  const { logCompleted } = useOnboardingStepTracking('frequency');
  const router = useRouter();
  const { t } = useLanguage();
  const insets = useSafeAreaInsets();
  const [selectedFrequency, setSelectedFrequency] = useState<FrequencyOption | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    Keyboard.dismiss();
    const loadSavedFrequency = async () => {
      const data = await TempOnboardingService.getData();
      if (data.addictionFrequency) {
        setSelectedFrequency(data.addictionFrequency as FrequencyOption);
        console.log('📥 Loaded previously saved frequency:', data.addictionFrequency);
      }
    };
    loadSavedFrequency();
  }, []);

  const frequencyOptions = [
    { value: 'once_week' as FrequencyOption, label: t('onboarding.frequency.options.onceWeek') },
    { value: 'two_three_week' as FrequencyOption, label: t('onboarding.frequency.options.twoThreeWeek') },
    { value: 'four_five_week' as FrequencyOption, label: t('onboarding.frequency.options.fourFiveWeek') },
    { value: 'daily' as FrequencyOption, label: t('onboarding.frequency.options.daily') },
    { value: 'already_sober' as FrequencyOption, label: t('onboarding.frequency.options.alreadySober') },
    { value: 'not_addicted' as FrequencyOption, label: t('onboarding.frequency.options.notAddicted') },
  ];

  const handleOptionSelect = (option: FrequencyOption) => {
    setSelectedFrequency(option);
  };

  const handleContinue = async () => {
    if (!selectedFrequency) {
      Alert.alert(
        t('onboarding.frequency.required'),
        t('onboarding.frequency.requiredMessage')
      );
      return;
    }

    setIsLoading(true);
    try {
      await TempOnboardingService.saveAddictionFrequency(selectedFrequency);
      console.log('✅ Frequency saved:', selectedFrequency);
      // PRD §5.2 — `onboarding_step_completed` for the `frequency` step.
      logCompleted();
      router.push('/onboarding-goal');
    } catch (error) {
      console.error('❌ Error saving frequency:', error);
      Alert.alert(
        t('onboarding.frequency.error'),
        t('onboarding.frequency.errorMessage')
      );
    } finally {
      setIsLoading(false);
    }
  };

  const progressWidth = 80; // Step 8 of 10

  return (
    <OnboardingLayout progressWidth={progressWidth}>
      <View style={styles.contentContainer}>
        <ScrollView style={styles.scrollView} showsVerticalScrollIndicator={false}>
          <View style={styles.section}>
            <View style={styles.stepBadge}>
              <Text style={styles.stepNumber}>8</Text>
            </View>

            <View style={styles.textContainer}>
              <Text style={styles.title}>{t('onboarding.frequency.title')}</Text>
            </View>
          </View>

          <View style={styles.optionsSection}>
            {frequencyOptions.map((option) => (
              <TouchableOpacity
                key={option.value}
                style={[
                  styles.card,
                  selectedFrequency === option.value && styles.selectedCard
                ]}
                onPress={() => handleOptionSelect(option.value)}
              >
                <Text style={styles.optionText}>
                  {option.label}
                </Text>
                <View style={[
                  styles.radioButton,
                  selectedFrequency === option.value && styles.selectedRadio
                ]}>
                  {selectedFrequency === option.value && <View style={styles.radioInner} />}
                </View>
              </TouchableOpacity>
            ))}
          </View>
        </ScrollView>

        <View style={[styles.buttonContainer, { bottom: insets.bottom + 24 }]}>
          <CircularArrowButton
            onPress={handleContinue}
            disabled={!selectedFrequency || isLoading}
          />
        </View>
      </View>
    </OnboardingLayout>
  );
}

const styles = StyleSheet.create({
  contentContainer: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
  },
  section: {
    padding: 24,
    paddingBottom: 0,
    alignItems: 'flex-start',
    gap: 24,
  },
  stepBadge: {
    backgroundColor: '#151515',
    borderRadius: 32,
    width: 40,
    height: 40,
    justifyContent: 'center',
    alignItems: 'center',
  },
  stepNumber: {
    color: '#FFFFFF',
    textAlign: 'center',
    fontFamily: 'Sentient-Regular',
    fontSize: 20,
    lineHeight: 28,
  },
  textContainer: {
    alignItems: 'flex-start',
    gap: 8,
  },
  title: {
    color: '#151515',
    fontFamily: 'Sentient-Regular',
    fontSize: 24,
    lineHeight: 28.8,
    width: 345,
  },
  optionsSection: {
    padding: 24,
    alignItems: 'flex-start',
    gap: 12,
    flexDirection: 'column',
    width: '100%',
  },
  card: {
    padding: 20,
    justifyContent: 'center',
    alignItems: 'center',
    gap: 4,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#F0EEE4',
    backgroundColor: '#FFFFFF',
    width: '100%',
    minHeight: 64,
    flexDirection: 'row',
    overflow: 'hidden',
  },
  selectedCard: {
    borderColor: '#004165',
    backgroundColor: '#E6EBF0',
  },
  optionText: {
    color: '#151515',
    flex: 1,
    fontFamily: 'Inter_400Regular',
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 19.6,
  },
  radioButton: {
    borderWidth: 1,
    borderColor: '#7E7D75',
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    width: 24,
    height: 24,
    justifyContent: 'center',
    alignItems: 'center',
    flexShrink: 0,
  },
  selectedRadio: {
    borderWidth: 3,
    borderColor: '#015480',
    backgroundColor: '#FFFFFF',
  },
  radioInner: {
    backgroundColor: '#015480',
    width: 12,
    height: 12,
    borderRadius: 6,
    flexShrink: 0,
  },
  buttonContainer: {
    position: 'absolute',
    right: 24,
  },
});
